import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const ParentsForm = () => {
  const [formData, setFormData] = useState({
    fatherName: '',
    motherName: '',
    phoneNo: '',
    phoneNo2:'',
    fatherDOB: '',
    motherDOB: '',
    fatherAge: '',
    motherAge: '',
    anniversary: '',
    fatherImage: null,
    motherImage: null,
    parentID: ''
  });
  const [parents, setParents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingParentId, setEditingParentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchParents = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/parents/getAllParents`);
      setParents(response.data);

      // Generate the next ParentID
      generateNextParentID(response.data);
    } catch (error) {
      console.error('Failed to fetch parents', error);
    }
  };
  
  const generateNextParentID = (parentsList) => {
    if (parentsList.length === 0) {
      setFormData(prevData => ({
        ...prevData,
        parentID: 'p00001'
      }));
    } else {
      // Get the last parentID and increment it
      const lastParent = parentsList.reduce((max, parent) =>
        parseInt(parent.parentID.slice(1)) > parseInt(max.parentID.slice(1)) ? parent : max
      );
      const lastID = parseInt(lastParent.parentID.slice(1));
      const nextID = `p${String(lastID + 1).padStart(5, '0')}`;
      setFormData(prevData => ({
        ...prevData,
        parentID: nextID
      }));
    }
  };


  useEffect(() => {
    fetchParents();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'file' ? files[0] : value,
    }));

    // Calculate and set age when DOB is entered
    if (name === 'fatherDOB') {
      const age = calculateAge(value);
      setFormData(prevData => ({
        ...prevData,
        fatherAge: age,
      }));
    } else if (name === 'motherDOB') {
      const age = calculateAge(value);
      setFormData(prevData => ({
        ...prevData,
        motherAge: age,
      }));
    }
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  const formDataToSubmit = new FormData();
  for (const key in formData) {
    formDataToSubmit.append(key, formData[key]);
  }

  try {
    if (isEditing) {
      // Update parent if editing
      const parentUpdateResponse = await axios.put(
        ` ${process.env.REACT_APP_API_BASE_URL}/api/parents/updateParent/${editingParentId}`, 
        formDataToSubmit, 
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      alert('Parent details updated successfully!');

      // Assuming the parent data includes a children array with child IDs
      const { fatherName, motherName, phoneNo, phoneNo2, children } = parentUpdateResponse.data;
      
      const updateStudentPromises = children.map((child) => {
        return axios.put(` ${process.env.REACT_APP_API_BASE_URL}/api/students/editParent/${child._id}`, {
          fatherName: fatherName,
          motherName: motherName,
          phoneNo: phoneNo,
          phoneNo2 : phoneNo2,
        });
      });
      
      // Wait for all student update requests to complete
      await Promise.all(updateStudentPromises);
    } else {
      // Create new parent
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/parents/createParent`, formDataToSubmit, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      alert('Parent details submitted successfully!');
    }

    fetchParents();
    setFormData({
      fatherName: '',
      motherName: '',
      phoneNo: '',
      phoneNo2: '',
      fatherDOB: '',
      motherDOB: '',
      fatherAge: '',
      motherAge: '',
      anniversary: '',
      fatherImage: null,
      motherImage: null,
      parentID: ''
    });
    setIsEditing(false);
  } catch (error) {
    alert('Failed to submit parent details. Please try again.');
    console.error(error);
  } finally {
    setLoading(false); // End loading
  }
};

  const handleEdit = (parent) => {
    setFormData({
      fatherName: parent.fatherName,
      motherName: parent.motherName,
      phoneNo: parent.phoneNo,
      phoneNo2: parent.phoneNo2,
      fatherDOB: parent.fatherDOB,
      motherDOB: parent.motherDOB,
      fatherAge: parent.fatherAge,
      motherAge: parent.motherAge,
      anniversary: parent.anniversary,
      fatherImage: null, // Set to null to avoid sending old file
      motherImage: null, // Set to null to avoid sending old file
      parentID: parent.parentID
    });
    setIsEditing(true);
    setEditingParentId(parent._id);
  };


  const handleView = (id) => {
    navigate(`/view-parent/${id}`);
  };

  const handleDelete = async (parentId) => {
    if (window.confirm("Are you sure you want to delete this parent?")) {
      try {
        await axios.delete(` ${process.env.REACT_APP_API_BASE_URL}/api/parents/deleteParent/${parentId}`);
        alert("Parent deleted successfully!");
        fetchParents(); // Refresh list after deletion
      } catch (error) {
        alert("Failed to delete parent. Please try again.");
      }
    }
  };

  const clearForm = () => {
    setFormData({
      fatherName: '',
      motherName: '',
      phoneNo: '',
      phoneNo2: '',
      fatherDOB: '',
      motherDOB: '',
      fatherAge: '',
      motherAge: '',
      anniversary: '',
      fatherImage: null,
      motherImage: null,
      parentID: ''
    });
    setIsEditing(false);
  }

  // Filter parents based on search query
  const filteredParents = parents.filter(parent =>
    parent.parentID.toLowerCase().includes(searchQuery.toLowerCase()) ||
    parent.fatherName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    parent.motherName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 bg-white shadow-md rounded-lg">
      <h2 className="text-2xl font-semibold mb-6">Parents Form</h2>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">

        <div>
          <label className="block text-sm font-medium text-gray-700">Father's Name</label>
          <input
            type="text"
            name="fatherName"
            value={formData.fatherName}
            onChange={handleChange}
            placeholder="Enter father's name"
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Mother's Name</label>
          <input
            type="text"
            name="motherName"
            value={formData.motherName}
            onChange={handleChange}
            placeholder="Enter mother's name"
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Phone Number</label>
          <input
            type="number"
            name="phoneNo"
            value={formData.phoneNo}
            onChange={handleChange}
            placeholder="Enter Phone Number"
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Alternate Phone Number</label>
          <input
            type="number"
            name="phoneNo2"
            value={formData.phoneNo2}
            onChange={handleChange}
            placeholder="Enter Alternate Phone Number"
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Father's DOB</label>
          <input
            type="date"
            name="fatherDOB"
            value={formData.fatherDOB}
            onChange={handleChange}
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Father's Age</label>
          <input
            type="text"
            name="fatherAge"
            value={formData.fatherAge}
            disabled
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Mother's DOB</label>
          <input
            type="date"
            name="motherDOB"
            value={formData.motherDOB}
            onChange={handleChange}
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Mother's Age</label>
          <input
            type="text"
            name="motherAge"
            value={formData.motherAge}
            disabled
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Father's Image</label>
          <input
            type="file"
            name="fatherImage"
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Mother's Image</label>
          <input
            type="file"
            name="motherImage"
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Anniversary Date</label>
          <input
            type="date"
            name="anniversary"
            value={formData.anniversary}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Parent ID</label>
          <input
            type="text"
            name="parentID"
            value={formData.parentID}
            disabled
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100"
          />
        </div>

        <div className="col-span-2">
          <button
            type="submit"
            className="w-1/6 px-4 py-2 bg-blue-800 text-white rounded-md shadow-md hover:bg-blue-600"
            disabled={loading}
          >
             {loading
              ? "Submitting..."
              : isEditing
              ? "Update"
              : "Submit"}
          </button>
          {isEditing && (
            <button
              onClick={clearForm}
              className="bg-gray-500 hover:bg-gray-400 ml-6 text-white py-2 px-4 rounded"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="max-w-7xl mx-auto">
        <header className="my-8">
          <h1 className="text-2xl md:text-4xl font-bold text-black">
            Parents List
          </h1>
        </header>
        <div className="bg-white shadow-md rounded-lg p-4 md:p-6 mb-6">
          <div className="mb-4 flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
            <div className="flex space-x-2 md:space-x-4 w-full md:w-auto">
              <input
                type="text"
                placeholder="Search by Parent ID, Father Name, or Mother Name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="p-2 border border-gray-300 rounded w-full md:w-96"
              />
            </div><div className="flex items-center bg-blue-100 text-blue-700 rounded px-4 py-2 w-full md:w-auto justify-between md:justify-start">
              <i className="fas fa-users text-2xl mr-2"></i>
              <p className="text-lg font-semibold">
                Total Parents: {filteredParents.length}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-300">
              <thead className="bg-gray-200">
                <tr>
                  <th className="py-2 px-2 md:px-4 border-b border-gray-300 text-xs md:text-base">Sr No.</th>
                  <th className="py-2 px-2 md:px-4 border-b border-gray-300 text-xs md:text-base">Parent ID</th>
                  <th className="py-2 px-2 md:px-4 border-b border-gray-300 text-xs md:text-base">Father Name</th>
                  <th className="py-2 px-2 md:px-4 border-b border-gray-300 text-xs md:text-base">Mother Name</th>
                  <th className="py-2 px-2 md:px-4 border-b border-gray-300 text-xs md:text-base">Children</th>
                  <th className="py-2 px-2 md:px-4 border-b border-gray-300 text-xs md:text-base">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-gray-50">
                {filteredParents.map((parent, index) => (
                  <tr key={parent._id} className="text-center hover:bg-gray-100">
                    <td className="py-2 px-2 md:px-4 border-b border-gray-300 text-xs md:text-base">{index + 1}</td>
                    <td className="py-2 px-2 md:px-4 border-b border-gray-300 text-xs md:text-base">{parent.parentID}</td>
                    <td className="py-2 px-2 md:px-4 border-b border-gray-300 text-xs md:text-base">{parent.fatherName}</td>
                    <td className="py-2 px-2 md:px-4 border-b border-gray-300 text-xs md:text-base">{parent.motherName}</td>
                    <td className="py-2 px-2 md:px-4 border-b border-gray-300 text-xs md:text-base">
                      {parent.children && parent.children.length > 0 ? (
                        <ul>
                          {parent.children.map((child, idx) => (
                            <li key={idx}>{`${child.firstName} ${child.lastName}`}</li>
                          ))}
                        </ul>
                      ) : (
                        'No children added'
                      )}
                    </td>
                    <td className="py-2 px-2 md:px-4 border-b border-gray-300">
                      <div className="flex flex-col md:flex-row justify-center gap-2 md:gap-4">
                        <button
                          onClick={() => navigate('/new-student', { state: { fatherName: parent.fatherName, motherName: parent.motherName, parentID: parent.parentID ,phoneNo:parent.phoneNo , phoneNo2 : parent.phoneNo2 } })}
                          className="px-2 py-1 bg-sky-500 text-white rounded hover:bg-sky-400 flex items-center justify-center"
                        >
                          <i className="fas fa-add mr-1"></i>Add Child
                        </button>
                        <button
                          onClick={() => handleView(parent._id)}
                          className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-500 flex items-center justify-center"
                        >
                          <i className="fas fa-eye mr-1"></i>View
                        </button>
                        <button
                          onClick={() => handleEdit(parent)}
                          className="bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-400 transition duration-200 flex items-center justify-center"
                        >
                          <i className="fas fa-edit mr-1"></i>Edit
                        </button>
                        <button
                          onClick={() => handleDelete(parent._id)}
                          className="bg-red-600 text-white rounded px-2 py-1 hover:bg-red-500 transition duration-200 flex items-center justify-center"
                        >
                          <i className="fas fa-trash mr-1"></i>Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentsForm;
